/*
 * Product page redesign — the hero gallery.
 *
 * The main picture is a row of slides that snap into place: swipe on a
 * phone, or use the arrows or thumbnails anywhere. Clicking a picture, or the
 * magnifier, opens every picture full screen. There a click zooms in on that
 * spot, moving the mouse (or dragging a finger) looks around, and another
 * click zooms back out.
 *
 * Choosing a bundle brings its own picture to the front. With bundle photos
 * on (data-pdp-bundle-photos, see pdp-gallery.liquid), pictures that belong
 * only to the other bundles leave the row while it's chosen.
 */
(function () {
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var ZOOM = 2;

  function clamp(n, lo, hi) {
    return Math.min(hi, Math.max(lo, n));
  }

  // Scroll a row of slides to slide i.
  function go(row, i, instant) {
    row.scrollTo({ left: i * row.clientWidth, behavior: instant || reduce ? 'auto' : 'smooth' });
  }

  // Which slide a row is showing.
  function at(row) {
    return row.clientWidth ? Math.round(row.scrollLeft / row.clientWidth) : 0;
  }

  // Slides after the first carry their picture in data-srcset/data-src
  // (pdp-gallery.liquid), so they don't compete with the first one, the
  // page's largest paint, while the page loads.
  function hydrate(img) {
    if (!img || !img.dataset.srcset) return;
    img.srcset = img.dataset.srcset;
    if (img.dataset.src) img.src = img.dataset.src;
    img.removeAttribute('data-srcset');
    img.removeAttribute('data-src');
  }

  // Start loading the pictures either side of slide i, so the next one is
  // ready before it slides in.
  function wake(slides, i) {
    for (var k = i - 1; k <= i + 1; k++) {
      var img = slides[k] && slides[k].querySelector('img');
      if (img && img.loading === 'lazy') img.loading = 'eager';
    }
  }

  // Takes an arrow away at the end of the row. If it had focus, focus goes
  // to the other arrow so a keyboard user is not stranded.
  function setEnd(arrow, atEnd, other) {
    if (!arrow) return;
    if (atEnd && document.activeElement === arrow && other) other.focus();
    arrow.disabled = atEnd;
  }

  // Runs fn at most once a frame while a row scrolls.
  function onScroll(row, fn) {
    var ticking = false;
    row.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          ticking = false;
          fn();
        });
      },
      { passive: true }
    );
  }

  function init(root) {
    var main = root.querySelector('[data-pdp-main]');
    var row = main && main.querySelector('[data-pdp-slides]');
    if (!row || row.dataset.pdpReady) return;
    row.dataset.pdpReady = '1';

    // Every slide and thumbnail, and the ones in the row now: a bundle's
    // pictures can be left out (hidden) while another bundle is chosen.
    var allSlides = Array.prototype.slice.call(row.querySelectorAll('[data-pdp-slide]'));
    var allThumbs = Array.prototype.slice.call(root.querySelectorAll('[data-pdp-thumb]'));
    var shownOnly = function (el) { return !el.hidden; };
    var slides = allSlides.filter(shownOnly);
    var thumbs = allThumbs.filter(shownOnly);
    var prev = main.querySelector('[data-pdp-prev]');
    var next = main.querySelector('[data-pdp-next]');
    var count = main.querySelector('[data-pdp-count]');
    var strip = root.querySelector('[data-pdp-strip]');
    var gallery = root.querySelector('.pdp-gallery');
    var bundlePhotos = !!(gallery && gallery.hasAttribute('data-pdp-bundle-photos'));
    var index = -1;

    // The held-back pictures come back once the page has loaded, or as soon
    // as anyone touches, focuses or scrolls the gallery (a variant change
    // scrolls it too), whichever is first.
    var hydrated = false;
    function hydrateAll() {
      if (hydrated) return;
      hydrated = true;
      slides.forEach(function (s) { hydrate(s.querySelector('img')); });
      wake(slides, Math.max(0, index));
    }
    ['pointerdown', 'touchstart', 'keydown', 'focusin'].forEach(function (type) {
      main.addEventListener(type, hydrateAll, { passive: true });
      if (strip) strip.addEventListener(type, hydrateAll, { passive: true });
    });
    if (document.readyState === 'complete') hydrateAll();
    else window.addEventListener('load', hydrateAll);

    // The thumbnail strip follows along, keeping the current picture's
    // thumbnail in the middle as far as its ends allow.
    function follow(thumb, instant) {
      if (!strip || !thumb) return;
      strip.scrollTo({
        left: thumb.offsetLeft - (strip.clientWidth - thumb.offsetWidth) / 2,
        behavior: instant || reduce ? 'auto' : 'smooth'
      });
    }

    function sync() {
      var i = at(row);
      if (i === index) return;
      var first = index === -1;
      index = i;
      if (i !== 0) hydrateAll();
      if (count) count.textContent = i + 1;
      setEnd(prev, i === 0, next);
      setEnd(next, i === slides.length - 1, prev);
      thumbs.forEach(function (t) {
        var on = Number(t.dataset.pdpThumb) === i;
        t.setAttribute('aria-current', on ? 'true' : 'false');
        if (on) follow(t, first);
      });
      wake(slides, i);
    }

    onScroll(row, sync);
    sync();

    // The full-screen view's slides, built the first time it opens (below).
    // Declared here so a bundle change can throw a built set away.
    var zSlides = null;

    // A bundle was chosen: its picture goes to the front of the row. With
    // bundle photos on, the other bundles' own pictures leave the row and
    // the rest keep the product's order; without, the order stays and the
    // row moves to that picture.
    function showBundle(mediaId) {
      var lead = allSlides.filter(function (s) { return s.dataset.mediaId === String(mediaId); })[0];
      if (!lead) return;

      if (!bundlePhotos) {
        var at = slides.indexOf(lead);
        if (at > -1) {
          hydrateAll();
          go(row, at);
        }
        return;
      }

      var ordered = allSlides.slice().sort(function (a, b) { return Number(a.dataset.pos) - Number(b.dataset.pos); });
      var shown = [lead].concat(ordered.filter(function (s) { return s !== lead && !s.hasAttribute('data-bundle'); }));
      var same = shown.length === slides.length && shown.every(function (s, i) { return s === slides[i]; });
      if (same) {
        go(row, 0);
        return;
      }

      var thumbFor = {};
      allThumbs.forEach(function (t) { thumbFor[t.dataset.mediaId] = t; });

      allSlides.forEach(function (s) { s.hidden = shown.indexOf(s) === -1; });
      shown.forEach(function (s, i) {
        row.appendChild(s);
        s.setAttribute('aria-label', i + 1 + ' of ' + shown.length);
      });
      slides = shown;

      thumbs = shown.map(function (s) { return thumbFor[s.dataset.mediaId]; }).filter(Boolean);
      allThumbs.forEach(function (t) {
        t.hidden = thumbs.indexOf(t) === -1;
        if (t.hidden) t.dataset.pdpThumb = '-1';
      });
      thumbs.forEach(function (t, i) {
        if (strip) strip.appendChild(t);
        t.dataset.pdpThumb = String(i);
        t.setAttribute('aria-label', 'Show picture ' + (i + 1) + ' of ' + thumbs.length);
      });

      root.querySelectorAll('[data-pdp-total], [data-pdp-zoom-total]').forEach(function (el) {
        el.textContent = shown.length;
      });

      // The full-screen view is built again from the new row when next opened.
      if (zSlides) {
        zSlides.forEach(function (z) { z.remove(); });
        zSlides = null;
      }

      // The chosen bundle's picture is wanted now, whatever else waits.
      hydrate(lead.querySelector('img'));
      if (hydrated) slides.forEach(function (s) { hydrate(s.querySelector('img')); });
      index = -1;
      go(row, 0, true);
      sync();
    }

    document.addEventListener('variant:change', function (event) {
      var detail = event.detail || {};
      if (detail.sectionId !== root.dataset.sectionId || !detail.variant) return;
      var media = detail.variant.featured_media;
      if (media && media.id) showBundle(media.id);
    });

    if (prev) prev.addEventListener('click', function () { hydrateAll(); go(row, Math.max(0, index - 1)); });
    if (next) next.addEventListener('click', function () { hydrateAll(); go(row, Math.min(slides.length - 1, index + 1)); });
    // Every thumbnail, including any a bundle change brings in later.
    allThumbs.forEach(function (t) {
      t.addEventListener('click', function () {
        var to = Number(t.dataset.pdpThumb);
        if (to < 0) return;
        // The standard page clicks the chosen variant's thumbnail as it
        // starts up; that alone shouldn't bring the other pictures in early.
        if (to !== index) hydrateAll();
        go(row, to);
      });
    });

    /* ----------------------------------------------------- full screen */

    var box = root.querySelector('[data-pdp-zoombox]');
    if (!box || typeof box.showModal !== 'function') return;

    var zRow = box.querySelector('[data-pdp-zoom-slides]');
    var zPrev = box.querySelector('[data-pdp-zoom-prev]');
    var zNext = box.querySelector('[data-pdp-zoom-next]');
    var zCount = box.querySelector('[data-pdp-zoom-count]');
    var zIndex = -1;
    var zoomed = null;

    // Built the first time it opens, so the page does not carry a second
    // set of pictures it may never need.
    function build() {
      if (zSlides) return;
      zSlides = slides.map(function (s) {
        var source = s.querySelector('img');
        var slide = document.createElement('div');
        slide.className = 'pdp-zoom__slide';
        var img = document.createElement('img');
        img.loading = 'lazy';
        img.decoding = 'async';
        img.draggable = false;
        img.alt = source ? source.alt : '';
        img.src = s.dataset.zoom || (source && source.currentSrc) || '';
        slide.appendChild(img);
        zRow.appendChild(slide);
        return slide;
      });
    }

    function zSync() {
      var i = at(zRow);
      if (i === zIndex) return;
      zIndex = i;
      unzoom();
      if (zCount) zCount.textContent = i + 1;
      setEnd(zPrev, i === 0, zNext);
      setEnd(zNext, i === zSlides.length - 1, zPrev);
      wake(zSlides, i);
    }

    function open(i) {
      build();
      box.showModal();
      document.documentElement.style.overflow = 'hidden';
      // The theme keeps a gutter for the scrollbar (scrollbar-gutter: stable),
      // which nothing can paint over; the layout allows for it so the view
      // still sits centred in the window. It is zero with overlay scrollbars.
      box.style.setProperty('--pdp-zoom-gutter', Math.max(0, window.innerWidth - box.clientWidth) + 'px');
      wake(zSlides, i);
      go(zRow, i, true);
      zIndex = -1;
      zSync();
      if (!reduce) box.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 200, easing: 'ease-out' });
    }

    function close() {
      unzoom();
      // The gallery behind follows to the last picture looked at.
      go(row, zIndex, true);
      var fade = null;
      var done = function () {
        box.close();
        document.documentElement.style.overflow = '';
        if (fade) fade.cancel();
      };
      if (reduce) return done();
      fade = box.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 160, easing: 'ease-in', fill: 'forwards' });
      fade.finished.then(done, done);
    }

    row.addEventListener('click', function (event) {
      var slide = event.target.closest('[data-pdp-slide]');
      if (slide) open(slides.indexOf(slide));
    });
    var zoomButton = main.querySelector('[data-pdp-zoom]');
    if (zoomButton) zoomButton.addEventListener('click', function () { open(index); });

    onScroll(zRow, zSync);
    if (zPrev) zPrev.addEventListener('click', function () { go(zRow, Math.max(0, zIndex - 1)); });
    if (zNext) zNext.addEventListener('click', function () { go(zRow, Math.min(zSlides.length - 1, zIndex + 1)); });
    box.querySelector('[data-pdp-zoom-close]').addEventListener('click', close);
    // Escape: fade out like the close button rather than vanish.
    box.addEventListener('cancel', function (event) {
      event.preventDefault();
      close();
    });
    box.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        var step = event.key === 'ArrowLeft' ? -1 : 1;
        go(zRow, clamp(zIndex + step, 0, zSlides.length - 1));
      }
    });

    /* ------------------------------------------------------------ zoom */

    // Where the pointer is over a picture, 0 to 100 each way, measured on
    // the picture's own box as it sits before any zoom.
    function spot(img, event) {
      var frame = img.parentNode.getBoundingClientRect();
      return {
        x: clamp(((event.clientX - frame.left - img.offsetLeft) / img.offsetWidth) * 100, 0, 100),
        y: clamp(((event.clientY - frame.top - img.offsetTop) / img.offsetHeight) * 100, 0, 100)
      };
    }

    // Zooming scales the picture about a point, so moving that point is how
    // it looks around.
    function look(x, y) {
      zoomed.x = clamp(x, 0, 100);
      zoomed.y = clamp(y, 0, 100);
      zoomed.img.style.transformOrigin = zoomed.x + '% ' + zoomed.y + '%';
    }

    function zoomAt(slide, event) {
      var img = slide.querySelector('img');
      var p = spot(img, event);
      zoomed = { slide: slide, img: img, x: p.x, y: p.y };
      look(p.x, p.y);
      img.style.transform = 'scale(' + ZOOM + ')';
      slide.classList.add('is-zoomed');
      box.classList.add('is-zoomed');
    }

    function unzoom() {
      if (!zoomed) return;
      zoomed.img.style.transform = '';
      zoomed.slide.classList.remove('is-zoomed');
      box.classList.remove('is-zoomed');
      zoomed = null;
    }

    var press = null;
    zRow.addEventListener('pointerdown', function (event) {
      var slide = event.target.closest('.pdp-zoom__slide');
      if (!slide) return;
      press = {
        x: event.clientX,
        y: event.clientY,
        slide: slide,
        ox: zoomed ? zoomed.x : 0,
        oy: zoomed ? zoomed.y : 0,
        moved: false
      };
      if (zoomed && event.pointerType !== 'mouse') zRow.setPointerCapture(event.pointerId);
    });

    zRow.addEventListener('pointermove', function (event) {
      if (!zoomed) return;
      // A mouse looks around just by moving: the zoom follows it.
      if (event.pointerType === 'mouse') {
        var p = spot(zoomed.img, event);
        look(p.x, p.y);
        return;
      }
      // A finger drags the picture: whatever is under it stays under it.
      if (!press) return;
      var dx = event.clientX - press.x;
      var dy = event.clientY - press.y;
      if (Math.abs(dx) + Math.abs(dy) > 6) press.moved = true;
      look(
        press.ox - (dx / (zoomed.img.offsetWidth * (ZOOM - 1))) * 100,
        press.oy - (dy / (zoomed.img.offsetHeight * (ZOOM - 1))) * 100
      );
    });

    // A click or tap that did not move zooms in there, or back out.
    zRow.addEventListener('pointerup', function (event) {
      if (!press) return;
      var tap = !press.moved && Math.abs(event.clientX - press.x) + Math.abs(event.clientY - press.y) <= 6;
      var slide = press.slide;
      press = null;
      if (!tap) return;
      if (zoomed) unzoom();
      else zoomAt(slide, event);
    });

    // A swipe to the next picture hands the gesture to the browser.
    zRow.addEventListener('pointercancel', function () {
      press = null;
    });
  }

  function boot() {
    document.querySelectorAll('.pdp-hero[data-section-id]').forEach(init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // Theme editor: re-run when the section is re-rendered.
  document.addEventListener('shopify:section:load', boot);
})();
